/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import Button from "@mui/material/Button";
import { ObjectId } from "mongodb";
import StateInterface from "../../../../redux-magic/state-interface";
import { setLoadingThunk } from "../../../../redux-magic/store";
import { Area, BHK, Price } from "../../../../redux-magic/sub-interfaces/sub-interfaces";
/* Component Imports */

import { useRouter } from "next/router";
// import PropertyCardConfigAndContact from "./sub-components/PropertyCardConfig";
// import PropertyCardCTA from "./sub-components/PropertyCardCTA";
import PropertyCardImage from "./sub-components/PropertyCardImage";
// import PropertyCardLocation from "./sub-components/PropertyCardLocation";
import PropertyCardPrice from "./sub-components/PropertyCardPrice";
import PropertyCardTitle from "./sub-components/PropertyCardTitle";
import PropertyCardType from "./sub-components/PropertyCardType";

/* Library Function Imports */

import PrepareConfigString from "../../../../lib/PrepareConfigString";
import PropertyBuilder from "./sub-components/PropertyBuilder";

/* Styled Components */

const PropertyLink = styled("a", {
	shouldForwardProp: (prop) => prop !== "source",
})<{ source: string }>(({ theme, source }) => ({
	textDecoration: "none",
	// width: source === "carousel" ? "auto" : "100%",
	color: theme.palette.mode == "dark" ? "#fff" : "#000",
	WebkitTapHighlightColor: "transparent",
	borderRadius: "1.5rem",
	"&:hover": {
		background: "transparent",
		textDecoration: "none",
	},
}));

const GradientBorderWrapper = styled("div")({
	position: "relative",
	borderRadius: "2rem",
	overflow: "visible",
	display: "inline-block",
});

const GradientBorder = styled("div")({
	content: '""',
	position: "absolute",
	inset: 0,
	borderRadius: "1.5rem",
	zIndex: 0,
	pointerEvents: "none",
	background: "linear-gradient(360deg, #B05920 0%, #FFD5B2 100%)",
	padding: 3,
	WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
	WebkitMaskComposite: "xor",
	maskComposite: "exclude",
});

const PropertyCardContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "source" && prop !== "isEditor",
})<{ source: string; isEditor: boolean }>(({ theme, source, isEditor }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	scrollSnapAlign: "start",
	flexShrink: 1,
	flexGrow: 1,
	borderRadius: "1.5rem",
	overflow: "hidden",
	transition: "all 0.2s ease",
	// gap: "1.5rem",
	background: "linear-gradient(0deg, #FFEBDB 0%, #FFF 100%)",
	[theme.breakpoints.down("md1190")]: {
		// padding: "1rem",
		width: "21.25rem",
		height: "27.063rem",
	},
	[theme.breakpoints.up("md1190")]: { width: "43.75rem", height: "15.875rem" },
}));

const PropertyCardInfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-around",
	alignItems: "space-around",
	padding: "1rem",
	gap: "1rem",
	// margin: "0rem 0rem 0rem 0rem",
	// borderRadius: "0px 0px 16px 16px",
	cursor: "pointer",
	[theme.breakpoints.down("md1190")]: {
		width: "19.25rem",
		height: "11.813rem",
	},
	[theme.breakpoints.up("md1190")]: {
		width: "24.5rem",
		height: "15.25rem",
	},
}));

const SubTextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
}));

const PriceAndTransactionContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	height: "auto",
	gap: "0.5rem",
}));

const PropertyCard = ({
	id,
	profile_context,
	session,
	dispatch,
	loading,
	_id,
	slug,
	transaction_type,
	property_type,
	title,
	images,
	location,
	published_status,
	price,
	area,
	bhk,
	no_of_seats,
	saved_content,
	created_by,
	assigned_to,
	isEditor,
	boosting,
	source,
	search_log_id,
	ad_text,
	login_flow_global_state,
}: {
	id?: string;
	profile_context: StateInterface["profile_context"];
	session: StateInterface["redux_session"];
	dispatch: Function;
	loading: boolean;
	_id: ObjectId | string;
	slug: string;
	transaction_type: string;
	property_type: Array<string>;
	title: string;
	images: string;
	published_status: string;
	location: string;
	price: Price;
	area: Area;
	bhk: BHK;
	no_of_seats: BHK;
	saved_content: boolean;
	created_by?: any;
	assigned_to?: StateInterface["single_business_profile"]["business_profile"]["assigned_to"];
	isEditor?: boolean | undefined;
	boosting: StateInterface["property"]["boost_information"];
	source?: string;
	search_log_id?: string | ObjectId;
	ad_text?: string;
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const router = useRouter();

	return (
		<React.Fragment>
			<PropertyLink
				id={
					(router.asPath === "/"
						? "home_page_"
						: router.asPath.includes("/search?")
							? "search_page_"
							: router.asPath.includes("properties")
								? "properties_page_"
								: "") +
					(router.asPath.includes("/search?") ? "properties_" : "featured_properties_") +
					slug
				}
				source={source ? source : ""}
				href={`${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
					`${process.env.PRODUCTION_URL || ""}${router.locale}/properties/${slug}${
						search_log_id ? `?search_log_id=${search_log_id}` : ""
					}`,
				)}`}
				rel="noopener"
				target="_self"
				referrerPolicy="no-referrer"
				title={title}
				style={{
					direction: router.locale === "ar-AE" ? "rtl" : "ltr",
				}}
				onClick={() => {
					dispatch(
						setLoadingThunk({
							source: "properties",
							loading: true,
						}),
					);
				}}
			>
				<GradientBorderWrapper>
					<GradientBorder />
					<PropertyCardContainer
						source={source ? source : ""}
						isEditor={isEditor !== undefined ? isEditor : false}
						id={id}
					>
						{/* Image */}

						<PropertyCardImage
							title={title}
							images={images}
						/>

						{/* Title */}

						<PropertyCardInfoContainer>
							{/*Builder Info */}
							<PropertyBuilder
								title={created_by.business_profile_id ? created_by.business_profile_id.title : ""}
								type={created_by.business_profile_id ? created_by.business_profile_id.business_profile_type : ""}
								images={created_by.business_profile_id.display_picture_url}
							/>
							<PriceAndTransactionContainer>
								{/* Price */}

								<PropertyCardPrice
									loading={loading}
									price={price}
								/>
								{/* title */}
								<PropertyCardTitle
									loading={loading}
									title={title !== "" || undefined ? title : "Unknown Title"}
								/>
								{/* Property Type */}
								<PropertyCardType
									loading={loading}
									config_gg={PrepareConfigString(transaction_type, property_type, bhk, no_of_seats, area)}
									propertyType={property_type ? property_type : []}
								/>
							</PriceAndTransactionContainer>

							<Button
								variant="contained"
								size="medium"
								fullWidth
								startIcon={<LocalPhoneOutlinedIcon />}
								sx={{
									height: "2.5rem",
									borderRadius: "0.75rem",
									textTransform: "none",
									fontWeight: 400,
									backgroundColor: "#B05911",
									color: "#fff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									"& .MuiButton-startIcon": {
										marginRight: "8px",
										display: "flex",
										alignItems: "center",
									},
									"&:hover": {
										backgroundColor: "#B05911",
									},
								}}
							>
								View number
							</Button>
						</PropertyCardInfoContainer>
					</PropertyCardContainer>
				</GradientBorderWrapper>
			</PropertyLink>
		</React.Fragment>
	);
};

export default PropertyCard;
